import { useMutation } from '@tanstack/react-query'
import { apiFetch } from './client'

export type CopilotTransport = 'pstn' | 'webrtc'

export interface StartCopilotCallRequest {
  phone: string
  name?: string
  notes?: string
  // Per-call rep transport. "pstn" (default) dials the rep's real phone;
  // "webrtc" takes the call in the browser via the Twilio Voice SDK.
  mode?: CopilotTransport
}

export interface StartCopilotCallResponse {
  success: boolean
  call_sid: string
  // Only present on the webrtc branch: the Voice token + client identity the
  // browser Device needs to register and auto-accept the rep leg. The browser
  // now registers off /copilot/voice-token BEFORE this POST (see
  // useCopilotVoiceToken), so these are ignored on the client — kept for
  // backward compatibility with the response shape.
  token?: string
  identity?: string
  mode?: CopilotTransport
}

export interface CopilotVoiceToken {
  token: string
  identity: string
}

// Standalone Voice token so the browser Device can register under
// ``copilot-{user_id}`` BEFORE the outbound ``client:`` leg is placed. Fetching
// it separately (rather than from the /calls/outbound response) is what closes
// the register-after-dial race that left the rep leg unrouted.
export function useCopilotVoiceToken() {
  return useMutation({
    mutationFn: () => apiFetch<CopilotVoiceToken>('/copilot/voice-token'),
  })
}

export function useStartCopilotCall() {
  return useMutation({
    mutationFn: (body: StartCopilotCallRequest) =>
      apiFetch<StartCopilotCallResponse>('/copilot/calls/outbound', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  })
}

export function useEndCopilotCall() {
  return useMutation({
    mutationFn: (callSid: string) =>
      apiFetch<{ success: boolean; call_sid: string }>(
        `/copilot/calls/${callSid}/end`,
        { method: 'POST' },
      ),
  })
}
